import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button,
  Paper, Alert, InputAdornment, IconButton, Tooltip, Stack, useTheme, alpha,
  Skeleton, CircularProgress, Chip, ToggleButton, ToggleButtonGroup, Menu,
  ListItemIcon, ListItemText
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Download as DownloadIcon, Money as MoneyIcon,
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Analytics as AnalyticsIcon,
  TableChart as TableChartIcon, ShowChart as ShowChartIcon, FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon, Description as CsvIcon, CompareArrows as CompareArrowsIcon,
  Warning as WarningIcon, CheckCircle as CheckCircleIcon, Save as SaveIcon
} from '@mui/icons-material';
import {
  DataGrid, GridColDef, GridRenderCellParams, GridToolbarContainer,
  GridToolbarQuickFilter, GridToolbarColumnsButton, GridToolbarDensitySelector, GridRowSelectionModel,
  GridColumnMenu, GridColumnMenuProps, GridActionsCellItem,
  useGridApiRef, GridCallbackDetails
} from '@mui/x-data-grid';

// NOTE: In a real-world application, the following would be split into separate files:
// - types.ts (for all interface definitions)
// - utils/formatters.ts (for formatCurrency, formatVariance, etc.)
// - hooks/useFinancialDataProcessor.ts (for the custom hook)
// - components/MetricCard.tsx
// - components/ProfessionalToolbar.tsx
// - components/FinancialMetricsDashboard.tsx
// - components/FinancialDataTable.tsx
// - components/AddTransactionForm.tsx

// Import the new analysis component
import ProfitLossAnalysis from '../../../hotels/components/ProfitLossAnalysis';
import { GridApiCommunity } from '@mui/x-data-grid/internals';

// --- TYPE DEFINITIONS ---
interface FinancialGridRow {
  id: string;
  categoryId: number;
  itemName: string;
  accountCode?: string;
  [year: string]: string | number | boolean | undefined | null;
}
interface Category { id: number; name: string; type: 'revenue' | 'expense' | 'other'; displayOrder: number; }
interface FinancialDataAPI {
  years: string[];
  categories: Category[];
  benchmarks?: { [key: string]: { industry: number; percentile50: number; }; };
}
interface ProfitLossStatementProps {
  financialData: FinancialDataAPI | null;
  gridRows: FinancialGridRow[];
  addFormCategory: number | '';
  addFormItemName: string;
  addFormYear: string;
  addFormAmount: string;
  saving: boolean;
  setAddFormCategory: (value: number | '') => void;
  setAddFormItemName: (value: string) => void;
  setAddFormYear: (value: string) => void;
  setAddFormAmount: (value: string) => void;
  handleAddNewItem: () => Promise<void>;
  handleDeleteClick: (row: FinancialGridRow) => void;
  handleBatchDelete?: (ids: string[]) => void;
  processRowUpdate: (newRow: FinancialGridRow, oldRow: FinancialGridRow) => FinancialGridRow | Promise<FinancialGridRow>;
  onProcessRowUpdateError: (error: Error) => void;
  loading: boolean;
  selectedHotel: string;
  setError: (value: string | null) => void;
  hasChanges: boolean;
  handleSaveAllChanges: (
    selectedHotelId: string,
    financialData: FinancialDataAPI | null,
    selectedDocType: string,
    setSuccess: (message: string | null) => void,
    setError: (message: string | null) => void,
    fetchFinancialData: (hotelId: string) => Promise<void>
  ) => Promise<void>;
}
interface EnhancedFinancialGridRow extends FinancialGridRow {
  categoryName: string;
  categoryType: 'revenue' | 'expense' | 'other';
}

// Add new interface for period grouping with three levels
interface PeriodGrouping {
  level: 'monthly' | 'quarterly' | 'yearly';
  groups: { [key: string]: string[] }; // e.g., {"2023": ["Jan 2023", "Feb 2023", ...]} or {"Q1 2023": ["Jan 2023", "Feb 2023", "Mar 2023"]}
}

// --- CONSTANTS & UTILS ---
const FINANCIAL_COLORS = {
  positive: '#1E88E5',
  negative: '#D32F2F',
  neutral: '#546E7A',
  warning: '#F57C00',
};

const formatCurrency = (value: number, compact: boolean = false) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: compact ? 1 : 0, maximumFractionDigits: compact ? 1 : 0, notation: compact && Math.abs(value) >= 1_000_000 ? 'compact' : 'standard' }).format(value);

const formatVariance = (current: number, previous: number) => {
  if (!previous) return { value: 'N/A', isPositive: true };
  const variance = ((current - previous) / Math.abs(previous)) * 100;
  return { value: `${variance >= 0 ? '+' : ''}${variance.toFixed(1)}%`, isPositive: variance >= 0 };
};

const formatPercentage = (value: number, total: number) => total ? `${((value / total) * 100).toFixed(1)}%` : '0.0%';

// --- BEST PRACTICES FOR P&L DATA HANDLING ---

// 1. PERIOD MANAGEMENT UTILITIES
const PeriodUtils = {
  // Normalize different period formats to standard format
  normalize: (period: string): string => {
    // Handle "Jan 2023" → "2023-01"
    const monthMatch = period.match(/^(\w+)\s*(\d{4})/i);
    if (monthMatch) {
      const monthMap: { [key: string]: string } = {
        'jan': '01', 'january': '01', 'feb': '02', 'february': '02',
        'mar': '03', 'march': '03', 'apr': '04', 'april': '04',
        'may': '05', 'jun': '06', 'june': '06',
        'jul': '07', 'july': '07', 'aug': '08', 'august': '08',
        'sep': '09', 'september': '09', 'oct': '10', 'october': '10',
        'nov': '11', 'november': '11', 'dec': '12', 'december': '12'
      };
      const month = monthMap[monthMatch[1].toLowerCase()];
      return `${monthMatch[2]}-${month}`;
    }

    // Handle "Q1 2023" → "2023-Q1"
    const quarterMatch = period.match(/^Q(\d+)\s*(\d{4})/i);
    if (quarterMatch) {
      return `${quarterMatch[2]}-Q${quarterMatch[1]}`;
    }

    // Handle "2023" → "2023"
    if (/^\d{4}$/.test(period)) {
      return period;
    }

    return period;
  },

  // Get optimal view based on data characteristics
  getOptimalView: (periods: string[], dataPoints: number): 'yearly' | 'quarterly' | 'monthly' => {
    const timeSpanInMonths = PeriodUtils.getTimeSpanInMonths(periods);

    if (timeSpanInMonths > 36) return 'yearly';     // 3+ years
    if (dataPoints > 12) return 'quarterly';        // Many periods
    return 'monthly';                               // Few periods
  },

  getTimeSpanInMonths: (periods: string[]): number => {
    if (periods.length === 0) return 0;

    const sortedPeriods = periods.sort();
    const first = new Date(sortedPeriods[0] + '-01');
    const last = new Date(sortedPeriods[sortedPeriods.length - 1] + '-01');

    return (last.getFullYear() - first.getFullYear()) * 12 +
           (last.getMonth() - first.getMonth());
  },

  // Format periods for display
  formatForDisplay: (period: string, viewType: 'yearly' | 'quarterly' | 'monthly'): string => {
    if (viewType === 'yearly') {
      return period; // "2023"
    }

    if (viewType === 'quarterly') {
      const match = period.match(/^(\d{4})-Q(\d+)$/);
      if (match) {
        return `Q${match[2]}-${match[1].slice(-2)}`;
      }
    }

    if (viewType === 'monthly') {
      const match = period.match(/^(\d{4})-(\d{2})$/);
      if (match) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(match[2]) - 1;
        return `${monthNames[monthIndex]}-${match[1].slice(-2)}`;
      }
    }

    return period;
  }
};

// 2. AGGREGATION STRATEGIES
const AggregationStrategies = {
  // Sum values (for revenue, expenses)
  sum: (values: number[]): number => {
    return values.reduce((sum, val) => sum + (val || 0), 0);
  },

  // Average values (for rates, percentages)
  average: (values: number[]): number => {
    const validValues = values.filter(val => val != null && !isNaN(val));
    return validValues.length > 0 ? validValues.reduce((sum, val) => sum + val, 0) / validValues.length : 0;
  },

  // Last value (for balances, positions)
  last: (values: number[]): number => {
    const validValues = values.filter(val => val != null && !isNaN(val));
    return validValues.length > 0 ? validValues[validValues.length - 1] : 0;
  }
};

// 3. FINANCIAL METRICS CALCULATOR
const FinancialMetricsCalculator = {
  calculateMetrics: (data: any[], periods: string[]) => {
    const metrics: { [period: string]: any } = {};

    periods.forEach(period => {
      const periodData = data.filter(item => item[period] != null);

      const revenue = AggregationStrategies.sum(
        periodData
          .filter(item => item.categoryType?.toLowerCase().includes('revenue'))
          .map(item => parseFloat(item[period]) || 0)
      );

      const expenses = AggregationStrategies.sum(
        periodData
          .filter(item => item.categoryType?.toLowerCase().includes('expense'))
          .map(item => Math.abs(parseFloat(item[period]) || 0))
      );

      const netIncome = revenue - expenses;
      const margin = revenue > 0 ? (netIncome / revenue) * 100 : 0;

      metrics[period] = {
        revenue,
        expenses,
        netIncome,
        margin,
        expenseRatio: revenue > 0 ? (expenses / revenue) * 100 : 0
      };
    });

    return metrics;
  }
};

// 4. ENHANCED VIEW MANAGEMENT
interface ViewConfig {
  level: 'yearly' | 'quarterly' | 'monthly';
  periods: string[];
  displayFormat: string;
  aggregationMethod: 'sum' | 'average' | 'last';
  showComparisons: boolean;
  showTrends: boolean;
}

const ViewManager = {
  createViewConfig: (
    rawPeriods: string[],
    requestedLevel: 'yearly' | 'quarterly' | 'monthly'
  ): ViewConfig => {
    const normalizedPeriods = rawPeriods.map(PeriodUtils.normalize);

    let periods: string[] = [];
    let aggregationMethod: 'sum' | 'average' | 'last' = 'sum';

    if (requestedLevel === 'yearly') {
      // Group by year
      const years = [...new Set(normalizedPeriods.map(p => p.split('-')[0]))];
      periods = years.sort();
    } else if (requestedLevel === 'quarterly') {
      // Group by quarter
      const quarters = new Set<string>();
      normalizedPeriods.forEach(period => {
        const [year, month] = period.split('-');
        const quarter = Math.ceil(parseInt(month) / 3);
        quarters.add(`${year}-Q${quarter}`);
      });
      periods = Array.from(quarters).sort();
    } else {
      // Monthly - use normalized periods directly
      periods = normalizedPeriods.sort();
    }

    return {
      level: requestedLevel,
      periods,
      displayFormat: requestedLevel,
      aggregationMethod,
      showComparisons: periods.length > 1,
      showTrends: periods.length > 2
    };
  }
};

// --- SIMPLIFIED & FUNCTIONAL P&L DATA PROCESSOR ---
const useFinancialDataProcessor = (financialData: FinancialDataAPI | null, gridRows: FinancialGridRow[]) => {
  // 1. CLEAN PERIOD PROCESSING
  const { rawPeriods, periodType, cleanPeriods } = useMemo(() => {
    if (!financialData?.years) return { rawPeriods: [], periodType: 'unknown', cleanPeriods: [] };

    const periods = [...financialData.years];

    // Detect what type of data we have
    const sample = periods[0] || '';
    let type: 'yearly' | 'monthly' | 'quarterly' | 'unknown' = 'unknown';

    if (/^\d{4}$/.test(sample)) {
      type = 'yearly';
    } else if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(sample)) {
      type = 'monthly';
    } else if (/^q[1-4]/i.test(sample)) {
      type = 'quarterly';
    }

    // Sort periods chronologically
    const sorted = periods.sort((a, b) => {
      if (type === 'yearly') {
        return parseInt(a) - parseInt(b);
      } else if (type === 'monthly') {
        const monthOrder: { [key: string]: number } = {
          'jan': 1, 'january': 1, 'feb': 2, 'february': 2, 'mar': 3, 'march': 3,
          'apr': 4, 'april': 4, 'may': 5, 'jun': 6, 'june': 6,
          'jul': 7, 'july': 7, 'aug': 8, 'august': 8, 'sep': 9, 'september': 9,
          'oct': 10, 'october': 10, 'nov': 11, 'november': 11, 'dec': 12, 'december': 12
        };

        const parseMonth = (period: string) => {
          const match = period.match(/^(\w+)\s*(\d{4})?/i);
          if (!match) return { month: 0, year: 0 };
          const monthName = match[1].toLowerCase();
          const year = match[2] ? parseInt(match[2]) : 2023;
          const month = monthOrder[monthName] || 0;
          return { month, year };
        };

        const aParsed = parseMonth(a);
        const bParsed = parseMonth(b);
        const aValue = aParsed.year * 100 + aParsed.month;
        const bValue = bParsed.year * 100 + bParsed.month;
        return aValue - bValue;
      }
      return a.localeCompare(b);
    });

    return { rawPeriods: periods, periodType: type, cleanPeriods: sorted };
  }, [financialData?.years]);

  // 2. ROLLUP FUNCTIONS
  const createRollups = useCallback((periods: string[], level: 'yearly' | 'quarterly' | 'monthly') => {
    console.log('createRollups called with:', { periods, level, periodType });

    if (level === 'monthly' && periodType === 'monthly') {
      // For monthly view, aggregate by month name across all years
      const monthRollups: { [key: string]: string[] } = {};

      periods.forEach(period => {
        console.log('Processing period for monthly aggregation:', period);

        let monthName = '';

        // Try multiple patterns for month-year formats
        const patterns = [
          /^(\w+)-(\d{2})$/i,             // "Jan-25", "Feb-25" (month-2digit year)
          /^(\w+)-(\d{4})$/i,            // "Jan-2025", "Feb-2025" (month-4digit year)
          /^(\w+)\s+(\d{2})$/i,          // "Jan 25", "Feb 25" (month space 2digit year)
          /^(\w+)\s+(\d{4})$/i,          // "Jan 2025", "Feb 2025" (month space 4digit year)
        ];

        for (const pattern of patterns) {
          const match = period.match(pattern);
          if (match) {
            monthName = match[1];
            console.log(`Matched monthly pattern ${pattern}, extracted month: ${monthName}`);
            break;
          }
        }

        if (monthName) {
          // Normalize month name to proper case
          const normalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1).toLowerCase();

          if (!monthRollups[normalizedMonth]) monthRollups[normalizedMonth] = [];
          monthRollups[normalizedMonth].push(period);
          console.log(`Added period ${period} to month ${normalizedMonth}`);
        } else {
          console.warn(`Could not extract month from period: ${period}`);
        }
      });

      console.log('Final monthly rollups:', monthRollups);
      return monthRollups;
    }

    if (level === 'monthly' || periodType !== 'monthly') {
      // No rollup needed - return original periods
      const result = periods.reduce((acc: { [key: string]: string[] }, p: string) => ({ ...acc, [p]: [p] }), {});
      console.log('No rollup needed, returning:', result);
      return result;
    }

    const rollups: { [key: string]: string[] } = {};

    if (level === 'yearly') {
      console.log('Creating yearly rollups from periods:', periods);

      // Roll up to years - handle multiple period formats
      periods.forEach(period => {
        console.log('Processing period for yearly rollup:', period);

        let year = '';

        // Try multiple patterns for month-year formats
        const patterns = [
          /^(\w+)-(\d{2})$/i,             // "Jan-25", "Feb-25" (month-2digit year)
          /^(\w+)-(\d{4})$/i,            // "Jan-2025", "Feb-2025" (month-4digit year)
          /^(\w+)\s+(\d{2})$/i,          // "Jan 25", "Feb 25" (month space 2digit year)
          /^(\w+)\s+(\d{4})$/i,          // "Jan 2025", "Feb 2025" (month space 4digit year)
          /^(\d{4})-(\d{2})$/,           // "2025-01" (ISO format)
          /^(\d{2})\/(\d{4})$/,          // "01/2025" (slash format)
          /(\d{4})$/,                    // Just "2025" at the end
        ];

        for (const pattern of patterns) {
          const match = period.match(pattern);
          if (match) {
            if (pattern === patterns[0] || pattern === patterns[1]) {
              // Month-year formats: "Jan-25", "Jan-2025"
              let yearStr = match[2];
              // Convert 2-digit year to 4-digit year
              if (yearStr.length === 2) {
                const twoDigitYear = parseInt(yearStr);
                // Assume years 00-30 are 20xx, years 31-99 are 19xx
                year = twoDigitYear <= 30 ? `20${yearStr}` : `19${yearStr}`;
              } else {
                year = yearStr;
              }
            } else if (pattern === patterns[2] || pattern === patterns[3]) {
              // Month space year formats: "Jan 25", "Jan 2025"
              let yearStr = match[2];
              // Convert 2-digit year to 4-digit year
              if (yearStr.length === 2) {
                const twoDigitYear = parseInt(yearStr);
                year = twoDigitYear <= 30 ? `20${yearStr}` : `19${yearStr}`;
              } else {
                year = yearStr;
              }
            } else if (pattern === patterns[4] || pattern === patterns[5]) {
              // Year-month or month/year formats
              year = match[1];
            } else if (pattern === patterns[6]) {
              // Just year
              year = match[1];
            }

            if (year) {
              console.log(`Matched pattern ${pattern}, extracted year: ${year} from period: ${period}`);
              break;
            }
          }
        }

        if (year) {
          if (!rollups[year]) rollups[year] = [];
          rollups[year].push(period);
          console.log(`Added period ${period} to year ${year}`);
        } else {
          console.warn(`Could not extract year from period: ${period}`);
        }
      });

      console.log('Final yearly rollups:', rollups);
    } else if (level === 'quarterly') {
      // Roll up to quarters
      const monthToQuarter: { [key: string]: number } = {
        'jan': 1, 'january': 1, 'feb': 1, 'february': 1, 'mar': 1, 'march': 1,
        'apr': 2, 'april': 2, 'may': 2, 'jun': 2, 'june': 2,
        'jul': 3, 'july': 3, 'aug': 3, 'august': 3, 'sep': 3, 'september': 3,
        'oct': 4, 'october': 4, 'nov': 4, 'november': 4, 'dec': 4, 'december': 4
      };

      periods.forEach(period => {
        console.log('Processing period for quarterly rollup:', period);

        let quarterYear = '';
        let monthName = '';

        // Try multiple patterns for month-year formats (same as yearly logic)
        const patterns = [
          /^(\w+)-(\d{2})$/i,             // "Jan-25", "Feb-25" (month-2digit year)
          /^(\w+)-(\d{4})$/i,            // "Jan-2025", "Feb-2025" (month-4digit year)
          /^(\w+)\s+(\d{2})$/i,          // "Jan 25", "Feb 25" (month space 2digit year)
          /^(\w+)\s+(\d{4})$/i,          // "Jan 2025", "Feb 2025" (month space 4digit year)
        ];

        for (const pattern of patterns) {
          const match = period.match(pattern);
          if (match) {
            monthName = match[1];
            let yearStr = match[2];

            // Convert 2-digit year to 4-digit year
            if (yearStr.length === 2) {
              const twoDigitYear = parseInt(yearStr);
              // Assume years 00-30 are 20xx, years 31-99 are 19xx
              quarterYear = twoDigitYear <= 30 ? `20${yearStr}` : `19${yearStr}`;
            } else {
              quarterYear = yearStr;
            }

            console.log(`Matched quarterly pattern ${pattern}, extracted month: ${monthName}, year: ${quarterYear}`);
            break;
          }
        }

        if (quarterYear && monthName) {
          const quarter = monthToQuarter[monthName.toLowerCase()] || 1;
          const quarterKey = `Q${quarter} ${quarterYear}`;

          if (!rollups[quarterKey]) rollups[quarterKey] = [];
          rollups[quarterKey].push(period);
          console.log(`Added period ${period} to quarter ${quarterKey}`);
        } else {
          console.warn(`Could not extract month/year from period: ${period}`);
        }
      });

      console.log('Final quarterly rollups:', rollups);
    }

    console.log('Returning rollups:', rollups);
    return rollups;
  }, [periodType]);

  // 3. ENHANCED ROWS WITH ROLLUP DATA
  const createEnhancedRows = useCallback((viewLevel: 'yearly' | 'quarterly' | 'monthly') => {
    if (!gridRows || !financialData) return [];

    const rollups = createRollups(cleanPeriods, viewLevel);

    return gridRows.map(row => {
      // Use backend categories only
      let category = null;
      let categoryType: 'revenue' | 'expense' | 'other' = 'other';

      if (financialData.categories) {
        const backendCategory = financialData.categories.find(c => c.id === row.categoryId);
        if (backendCategory) {
          category = backendCategory;
          // Determine type from backend category_group
          const backendCat = backendCategory as any;
          if (backendCat.category_group === 'Revenue') {
            categoryType = 'revenue';
          } else if (backendCat.category_group === 'Expenses') {
            categoryType = 'expense';
          } else {
            // Fallback to name-based detection
            const name = backendCategory.name.toLowerCase();
            if (name.includes('revenue') || name.includes('income')) {
              categoryType = 'revenue';
            } else if (name.includes('expense') || name.includes('cost') || name.includes('charges')) {
              categoryType = 'expense';
            }
          }
        }
      }

      const enhancedRow = { ...row };

      // Add rollup calculations
      Object.entries(rollups).forEach(([rollupKey, sourcePeriods]) => {
        const total = sourcePeriods.reduce((sum, period) => {
          const value = row[period];
          const numValue = typeof value === 'number' ? value :
                          typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) || 0 : 0;
          return sum + numValue;
        }, 0);
        enhancedRow[rollupKey] = total;
      });

      return {
        ...enhancedRow,
        categoryName: category?.name || row.itemName,
        categoryType: categoryType
      };
    }).sort((a, b) => {
      // Sort by category type first (revenue before expenses), then by name
      if (a.categoryType !== b.categoryType) {
        if (a.categoryType === 'revenue') return -1;
        if (b.categoryType === 'revenue') return 1;
        return 0;
      }
      return a.categoryName.localeCompare(b.categoryName);
    });
  }, [gridRows, financialData, cleanPeriods, createRollups]);

  // 4. FINANCIAL METRICS CALCULATOR
  const calculateMetrics = useCallback((rows: any[], periods: string[]) => {
    const metrics: { [period: string]: any } = {};

    periods.forEach(period => {
      const revenueRows = rows.filter(r => r.categoryType?.toLowerCase().includes('revenue'));
      const expenseRows = rows.filter(r => r.categoryType?.toLowerCase().includes('expense'));

      const parseValue = (value: any): number => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const cleanValue = value.replace(/[^0-9.-]/g, '');
          return parseFloat(cleanValue) || 0;
        }
        return 0;
      };

      const revenue = revenueRows.reduce((sum, r) => {
        const value = parseValue(r[period]);
        return sum + (value > 0 ? value : 0);
      }, 0);

      const expense = expenseRows.reduce((sum, r) => {
        const value = parseValue(r[period]);
        return sum + Math.abs(value);
      }, 0);

      const netIncome = revenue - expense;
      const operatingMargin = revenue > 0 ? (netIncome / revenue) * 100 : 0;

      metrics[period] = { revenue, expense, netIncome, operatingMargin };
    });

    return metrics;
  }, []);

  return {
    periodType,
    rawPeriods,
    cleanPeriods,
    createRollups,
    createEnhancedRows,
    calculateMetrics
  };
};


// --- REUSABLE CHILD COMPONENTS ---

const MetricCard: React.FC<{ title: string; value: number | string; variance?: { value: string; isPositive: boolean }; subtitle?: string; icon?: React.ReactNode; benchmark?: number; }> =
  ({ title, value, variance, subtitle, icon, benchmark }) => {
    const theme = useTheme();
    return (
      <Paper elevation={0} sx={{ p: 3, flex: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, transition: 'all 0.2s ease-in-out', '&:hover': { boxShadow: theme.shadows[2], transform: 'translateY(-2px)' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>{title}</Typography>
            {subtitle && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
          </Box>
          {icon && <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>{icon}</Box>}
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: typeof value === 'number' ? (value >= 0 ? 'text.primary' : FINANCIAL_COLORS.negative) : 'text.primary' }}>
          {typeof value === 'number' ? formatCurrency(value, true) : value}
        </Typography>
        {variance && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {variance.isPositive ? <TrendingUpIcon fontSize="small" sx={{ color: FINANCIAL_COLORS.positive }} /> : <TrendingDownIcon fontSize="small" sx={{ color: FINANCIAL_COLORS.negative }} />}
            <Typography variant="body2" fontWeight={600} color={variance.isPositive ? FINANCIAL_COLORS.positive : FINANCIAL_COLORS.negative}>{variance.value}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>vs prior</Typography>
          </Box>
        )}
        {benchmark !== undefined && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary">Industry Benchmark: {formatCurrency(benchmark, true)}</Typography>
          </Box>
        )}
      </Paper>
    );
  };

const ProfessionalToolbar: React.FC<{ onExport: (format: 'csv' | 'pdf') => void }> = ({ onExport }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  return (
    <GridToolbarContainer sx={{ p: 2, borderBottom: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
      <GridToolbarQuickFilter variant="outlined" size="small" sx={{ minWidth: { xs: 1, sm: 300 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>Export</Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => { onExport('csv'); setAnchorEl(null); }}><ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon><ListItemText>Export as CSV</ListItemText></MenuItem>
          <MenuItem onClick={() => { onExport('pdf'); setAnchorEl(null); }}><ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon><ListItemText>Export as PDF</ListItemText></MenuItem>
        </Menu>
      </Box>
    </GridToolbarContainer>
  );
};

const FinancialMetricsDashboard: React.FC<{
  financialData: FinancialDataAPI | null;
  financialMetrics: any;
  years: string[];
  periodType?: string;
}> = ({ financialData, financialMetrics, years, periodType = 'yearly' }) => {
  const [selectedPeriod, setSelectedPeriod] = React.useState<string>(years[0]);

  React.useEffect(() => {
    if (years.length > 0) {
      setSelectedPeriod(prev => (years.includes(prev) ? prev : years[0]));
    }
  }, [years]);

  const currentIndex = years.indexOf(selectedPeriod);
  const currentPeriod = selectedPeriod;

  // Smart previous period calculation based on period type
  const getPreviousPeriod = (currentIdx: number, periods: string[], type: string) => {
    if (type === 'monthly') {
      // For monthly data, compare to same month previous year or previous month
      const current = periods[currentIdx];
      if (!current) return null;

      // Try to find same month previous year first
      const currentMatch = current.match(/^(\w+)\s*(\d{4})?/i);
      if (currentMatch && currentMatch[2]) {
        const monthName = currentMatch[1];
        const year = parseInt(currentMatch[2]);
        const previousYearPeriod = `${monthName} ${year - 1}`;

        if (periods.includes(previousYearPeriod)) {
          return previousYearPeriod;
        }
      }

      // Fallback to previous period in array
      return periods[currentIdx + 1] || null;
    } else {
      // For yearly/quarterly, use next index (which is previous due to desc sort)
      return periods[currentIdx + 1] || null;
    }
  };

  const previousPeriod = getPreviousPeriod(currentIndex, years, periodType);

  if (!currentPeriod || !financialMetrics[currentPeriod]) return null;

  const currentMetrics = financialMetrics[currentPeriod];
  const previousMetrics = previousPeriod ? financialMetrics[previousPeriod] : undefined;
  const netIncomeVariance = previousMetrics ? formatVariance(currentMetrics.netIncome, previousMetrics.netIncome) : undefined;

  // Determine the appropriate label for the selector
  const getSelectorLabel = (type: string) => {
    switch (type) {
      case 'monthly': return 'Period';
      case 'quarterly': return 'Quarter';
      case 'yearly': return 'Year';
      default: return 'Period';
    }
  };

  // Format period display for better readability
  const formatPeriodDisplay = (period: string, type: string) => {
    if (type === 'monthly') {
      // Convert monthly periods to "Jan-25" format for consistency
      const match = period.match(/^(\w+)\s*(\d{4})?/i);
      if (match) {
        const monthMap: { [key: string]: string } = {
          'jan': 'Jan', 'january': 'Jan', 'feb': 'Feb', 'february': 'Feb',
          'mar': 'Mar', 'march': 'Mar', 'apr': 'Apr', 'april': 'Apr',
          'may': 'May', 'jun': 'Jun', 'june': 'Jun',
          'jul': 'Jul', 'july': 'Jul', 'aug': 'Aug', 'august': 'Aug',
          'sep': 'Sep', 'september': 'Sep', 'oct': 'Oct', 'october': 'Oct',
          'nov': 'Nov', 'november': 'Nov', 'dec': 'Dec', 'december': 'Dec'
        };
        const monthKey = match[1].toLowerCase();
        const shortMonth = monthMap[monthKey] || match[1];
        if (match[2]) {
          const shortYear = match[2].slice(-2); // Get last 2 digits
          return `${shortMonth}-${shortYear}`;
        }
        return shortMonth;
      }
    } else if (type === 'quarterly') {
      // Format quarterly periods as "Q1-25"
      const match = period.match(/^(Q\d+)\s*(\d{4})/i);
      if (match) {
        const quarter = match[1];
        const year = match[2];
        const quarterShortYear = year.slice(-2);
        return `${quarter}-${quarterShortYear}`;
      }
    } else if (type === 'yearly') {
      // Format yearly as full year "2025" for better clarity
      if (/^\d{4}$/.test(period)) {
        return period; // Return full 4-digit year
      }
    }
    return period; // Return as-is for other types
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Period Selector - Enhanced for different period types */}
      {years.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ alignSelf: 'center' }}>
            {getSelectorLabel(periodType)}:
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={selectedPeriod}
            exclusive
            onChange={(_, v) => v && setSelectedPeriod(v)}
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: periodType === 'monthly' ? '0.75rem' : '0.875rem',
                px: periodType === 'monthly' ? 1 : 1.5,
                color: 'text.primary',
                borderColor: 'divider',
                '&.Mui-selected': {
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }
            }}
          >
            {years.map(period => (
              <ToggleButton key={period} value={period}>
                {formatPeriodDisplay(period, periodType)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <MetricCard
          title="Net Income"
          subtitle={formatPeriodDisplay(currentPeriod, periodType)}
          value={currentMetrics.netIncome}
          variance={netIncomeVariance}
          icon={<MoneyIcon />}
          benchmark={financialData?.benchmarks?.netIncome?.industry}
        />
        <MetricCard
          title="Total Revenue"
          subtitle={formatPeriodDisplay(currentPeriod, periodType)}
          value={currentMetrics.revenue}
          variance={previousMetrics ? formatVariance(currentMetrics.revenue, previousMetrics.revenue) : undefined}
          icon={<TrendingUpIcon />}
        />
        <MetricCard
          title="Operating Margin"
          subtitle={formatPeriodDisplay(currentPeriod, periodType)}
          value={`${currentMetrics.operatingMargin.toFixed(1)}%`}
          icon={<AnalyticsIcon />}
          benchmark={financialData?.benchmarks?.operatingMargin?.percentile50}
        />
        <MetricCard
          title="Expense Ratio"
          subtitle={formatPeriodDisplay(currentPeriod, periodType)}
          value={currentMetrics.revenue > 0 ? `${((currentMetrics.expense / currentMetrics.revenue) * 100).toFixed(1)}%` : 'N/A'}
          icon={<CompareArrowsIcon />}
        />
      </Stack>

      {netIncomeVariance && (
        <Alert severity={netIncomeVariance.isPositive ? 'success' : 'warning'} icon={netIncomeVariance.isPositive ? <CheckCircleIcon /> : <WarningIcon />}>
          <Typography variant="body2">
            {netIncomeVariance.isPositive ? 'Strong performance' : 'Performance attention'}: Net income has a variance of <strong>{netIncomeVariance.value}</strong> vs {
              periodType === 'monthly' ? 'previous comparable period' : 'prior period'
            }.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

const FinancialDataTable: React.FC<{
  rows: EnhancedFinancialGridRow[];
  years: string[];
  financialMetrics: any;
  categories: Category[];
  loading: boolean;
  saving: boolean;
  handleDeleteClick: (row: FinancialGridRow) => void;
  handleBatchDelete?: (ids: string[]) => void;
  onEnhancedDelete?: (ids: string[]) => void;
  processRowUpdate: (newRow: FinancialGridRow, oldRow: FinancialGridRow) => any;
  onProcessRowUpdateError: (error: Error) => void;
  columnMenu?: React.FC<GridColumnMenuProps>;
  selectedRows: GridRowSelectionModel;
  onRowSelectionChange: (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void;
}> = ({ rows, years, financialMetrics, categories, handleDeleteClick, handleBatchDelete, columnMenu, selectedRows, onRowSelectionChange, ...props }) => {
  const [paginationModel, setPaginationModel] = useState({ pageSize: 50, page: 0 });
  const theme = useTheme();
  const apiRef = useGridApiRef();

  // All columns use flex sizing only, no fixed width
  const columns = useMemo<GridColDef<EnhancedFinancialGridRow>[]>(() => {
    const baseCols: GridColDef<EnhancedFinancialGridRow>[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        resizable: false,
        cellClassName: 'actions',
        getActions: ({ row }: { row: EnhancedFinancialGridRow }) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteClick(row)}
            color="inherit"
            sx={{ color: 'error.main' }}
          />,
        ],
      },
      {
        field: 'categoryId',
        headerName: 'Category',
        flex: 1.5,
        minWidth: 150,
        editable: true,
        type: 'singleSelect',
        valueOptions: categories.map((c: Category) => ({ value: c.id, label: c.name })) as Array<{ value: number; label: string }>,
        valueFormatter: (params: { value: number }) => {
          const cat = categories.find(c => c.id === params.value);
          return cat ? cat.name : '';
        },
        renderCell: (params: GridRenderCellParams) => {
          const cat = categories.find(c => c.id === params.value);
          if (!cat) return null;

                    // Improved category type detection using backend category_group
          let categoryType: 'revenue' | 'expense' | 'other' = 'other';

          // Check if this is a backend category with category_group field
          const backendCat = cat as any;
          if (backendCat.category_group === 'Revenue') {
            categoryType = 'revenue';
          } else if (backendCat.category_group === 'Expenses') {
            categoryType = 'expense';
          } else if (cat.type) {
            // Fallback to type field if available
            categoryType = cat.type;
          } else {
            // Last resort: check the category name for keywords
            const catName = cat.name.toLowerCase();
            if (catName.includes('revenue') || catName.includes('income') || catName.includes('sales')) {
              categoryType = 'revenue';
            } else if (catName.includes('expense') || catName.includes('cost') || catName.includes('cogs') ||
                      catName.includes('operating') || catName.includes('administrative') ||
                      catName.includes('utilities') || catName.includes('maintenance') ||
                      catName.includes('charges') || catName.includes('departmental')) {
              categoryType = 'expense';
            }
          }

          const isRevenue = categoryType === 'revenue';
          const isExpense = categoryType === 'expense';

          console.log(`Category: ${cat.name}, Type: ${categoryType}, isRevenue: ${isRevenue}, isExpense: ${isExpense}`);

          // Define colors more explicitly
          let chipProps = {
            color: 'default' as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
            variant: 'outlined' as 'filled' | 'outlined',
            sx: {}
          };

          if (isRevenue) {
            chipProps = {
              color: 'success',
              variant: 'filled',
              sx: {
                bgcolor: '#e8f5e8 !important',
                color: '#2e7d32 !important',
                border: '1px solid #4caf50 !important',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#c8e6c9 !important',
                }
              }
            };
          } else if (isExpense) {
            chipProps = {
              color: 'error',
              variant: 'filled',
              sx: {
                bgcolor: '#ffebee !important',
                color: '#d32f2f !important',
                border: '1px solid #f44336 !important',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#ffcdd2 !important',
                }
              }
            };
          } else {
            chipProps = {
              color: 'default',
              variant: 'outlined',
              sx: {
                color: '#546E7A',
                borderColor: '#546E7A',
                fontWeight: 500,
              }
            };
          }

          const chipLabel = `${cat.name} (${isRevenue ? 'Revenue' : isExpense ? 'Expense' : 'Other'})`;

          return (
            <Tooltip title={chipLabel}>
              <Chip
                label={cat.name}
                size="small"
                color={chipProps.color}
                variant={chipProps.variant}
                sx={chipProps.sx}
              />
            </Tooltip>
          );
        }
      },
      { field: 'itemName', headerName: 'Description', flex: 2, minWidth: 120, editable: true }
    ];

    // Helper function to format period headers
    const formatPeriodHeader = (period: string): string => {
      // Check for aggregated month names (Jan, Feb, Mar, etc.)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (monthNames.includes(period)) {
        return period; // Return as-is for aggregated months
      }

      // Check if this looks like a monthly period (Jan 2023, January 2025, etc.)
      const monthYearMatch = period.match(/^(\w+)\s*(\d{4})/i);
      if (monthYearMatch) {
        const monthName = monthYearMatch[1].toLowerCase();
        const year = monthYearMatch[2];

        // Convert to short month format with 2-digit year
        const monthMap: { [key: string]: string } = {
          'jan': 'Jan', 'january': 'Jan', 'feb': 'Feb', 'february': 'Feb',
          'mar': 'Mar', 'march': 'Mar', 'apr': 'Apr', 'april': 'Apr',
          'may': 'May', 'jun': 'Jun', 'june': 'Jun',
          'jul': 'Jul', 'july': 'Jul', 'aug': 'Aug', 'august': 'Aug',
          'sep': 'Sep', 'september': 'Sep', 'oct': 'Oct', 'october': 'Oct',
          'nov': 'Nov', 'november': 'Nov', 'dec': 'Dec', 'december': 'Dec'
        };

        const shortMonth = monthMap[monthName] || monthName;
        const shortYear = year.slice(-2); // Get last 2 digits
        return `${shortMonth}-${shortYear}`;
      }

      // For quarterly periods (Q1 2023, etc.)
      const quarterMatch = period.match(/^(Q\d+)\s*(\d{4})/i);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const year = quarterMatch[2];
        const quarterShortYear = year.slice(-2);
        return `${quarter}-${quarterShortYear}`;
      }

      // For yearly periods, return full 4-digit year
      if (/^\d{4}$/.test(period)) {
        return period; // Return full 4-digit year
      }

      // Default: return period as-is
      return period;
    };

    const yearCols: GridColDef<EnhancedFinancialGridRow>[] = years.map(year => ({
      field: year,
      headerName: formatPeriodHeader(year), // Use formatted header
      flex: 1, // share space equally
      minWidth: 80, // Slightly larger min width for formatted headers
      editable: true,
      align: 'right',
      headerAlign: 'right',
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number;
        if (value == null) return <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>;
        const totalRevenue = financialMetrics[year]?.revenue || 0;
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <Typography variant="body2" fontWeight={500} color={value >= 0 ? 'text.primary' : FINANCIAL_COLORS.negative}>{formatCurrency(value)}</Typography>
            {params.row.categoryType !== 'revenue' && totalRevenue > 0 && <Typography variant="caption" color="text.secondary">{formatPercentage(Math.abs(value), totalRevenue)}</Typography>}
          </Box>
        );
      },
    }));

    return [...baseCols, ...yearCols];
  }, [years, categories, financialMetrics, theme]);

  const handleExport = useCallback((format: 'csv' | 'pdf') => { console.log(`Exporting as ${format}`); /* export logic */ }, []);

  const handleBatchDeleteClick = async () => {
    if (!handleBatchDelete || selectedRows.length === 0) {
      console.warn('No rows selected or handleBatchDelete not available');
      return;
    }

    try {
      console.log('Batch deleting rows:', selectedRows);

      // Use the enhanced delete handler if available, otherwise use the original
      if (props.onEnhancedDelete) {
        props.onEnhancedDelete(selectedRows as string[]);
      } else {
        // Call the batch delete function
        await handleBatchDelete(selectedRows as string[]);
      }

      // Clear selection after successful deletion
      onRowSelectionChange([], { api: apiRef.current as GridApiCommunity });

      console.log('Batch delete completed successfully');
    } catch (error) {
      console.error('Error during batch delete:', error);
      // Don't clear selection if there was an error
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Show Delete Selected button only when rows are selected */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" color="error" onClick={handleBatchDeleteClick}>
            Delete Selected ({selectedRows.length})
          </Button>
        </Box>
      )}
      <Paper elevation={0} sx={{ width: '100%', borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <DataGrid<EnhancedFinancialGridRow>
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          editMode="cell"
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          processRowUpdate={props.processRowUpdate}
          onProcessRowUpdateError={props.onProcessRowUpdateError}
          disableRowSelectionOnClick
          slots={{ toolbar: () => <ProfessionalToolbar onExport={handleExport} />, columnMenu: columnMenu }}
          autoHeight
          checkboxSelection
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={onRowSelectionChange}
          sx={{ width: '100%', border: 'none', '& .MuiDataGrid-columnHeaders': { bgcolor: alpha(theme.palette.background.default, 0.8) } }}
          onCellClick={(params, event) => {
            // Only start edit mode if the cell is editable and not a checkbox
            if (params.field !== '__check__' && apiRef.current.getCellMode(params.id, params.field) !== 'edit') {
              apiRef.current.startCellEditMode({ id: params.id, field: params.field });
            }
          }}
        />
      </Paper>
    </Box>
  );
};

const AddTransactionForm: React.FC<{
  formState: { category: number | ''; itemName: string; year: string; amount: string; };
  setters: { setCategory: (v: any) => void; setItemName: (v: string) => void; setYear: (v: string) => void; setAmount: (v: string) => void; };
  categories: Category[];
  saving: boolean;
  onSubmit: () => void;
}> = ({ formState, setters, categories, saving, onSubmit }) => {
  const theme = useTheme();
  const isFormInvalid = !formState.category || !formState.itemName || !formState.year || !formState.amount;

    // Group categories by type using backend categories
  const revenueCategories = categories.filter(cat => {
    const backendCat = cat as any;
    return backendCat.category_group === 'Revenue' ||
           cat.name.toLowerCase().includes('revenue');
  });
  const expenseCategories = categories.filter(cat => {
    const backendCat = cat as any;
    return backendCat.category_group === 'Expenses' ||
           cat.name.toLowerCase().includes('expense') ||
           cat.name.toLowerCase().includes('charges');
  });

  return (
    <Paper elevation={0} sx={{ mt: 3, p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>Add Transaction</Typography>
      <Stack component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <FormControl size="small" fullWidth sx={{ minWidth: 200, flex: 1.5 }} required>
          <InputLabel>Category</InputLabel>
          <Select value={formState.category} onChange={e => setters.setCategory(e.target.value)} label="Category">
            <MenuItem disabled>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Revenue Categories
              </Typography>
            </MenuItem>
            {revenueCategories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#4caf50'
                  }} />
                  {cat.name}
                </Box>
              </MenuItem>
            ))}
            <MenuItem disabled>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Expense Categories
              </Typography>
            </MenuItem>
            {expenseCategories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#f44336'
                  }} />
                  {cat.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Description" value={formState.itemName} onChange={e => setters.setItemName(e.target.value)} size="small" required fullWidth sx={{ flex: 2 }} />
        <TextField label="Period" value={formState.year} onChange={e => setters.setYear(e.target.value)} size="small" required sx={{ width: { xs: '100%', sm: 120 } }} />
        <TextField label="Amount" value={formState.amount} onChange={e => setters.setAmount(e.target.value)} size="small" type="number" required
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: { xs: '100%', sm: 150 } }} />
        <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <AddIcon />} disabled={saving || isFormInvalid} sx={{ height: 40, width: { xs: '100%', sm: 'auto' } }}>
          {saving ? 'Adding...' : 'Add Item'}
        </Button>
      </Stack>
    </Paper>
  );
};


// --- MAIN PARENT COMPONENT ---

// This is the main component definition you asked about.
// It now acts as a "container" that manages state and assembles the child components.
const ProfitLossStatement: React.FC<ProfitLossStatementProps> = ({
  financialData, gridRows, addFormCategory, addFormItemName, addFormYear, addFormAmount, saving,
  setAddFormCategory, setAddFormItemName, setAddFormYear, setAddFormAmount,
  handleAddNewItem, handleDeleteClick, handleBatchDelete, processRowUpdate, onProcessRowUpdateError, loading, selectedHotel, setError,
  hasChanges, handleSaveAllChanges
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'analysis'>('table');
  const [periodView, setPeriodView] = useState<'yearly' | 'quarterly' | 'monthly'>('monthly');
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [hasDeletions, setHasDeletions] = useState(false);



  // Use the simplified data processor
  const { periodType, rawPeriods, cleanPeriods, createRollups, createEnhancedRows, calculateMetrics } = useFinancialDataProcessor(financialData, gridRows);

  // Auto-set optimal view based on data
  React.useEffect(() => {
    if (periodType === 'yearly') {
      setPeriodView('yearly');
    } else if (periodType === 'monthly' && cleanPeriods.length > 12) {
      setPeriodView('quarterly');
    } else {
      setPeriodView('monthly');
    }
  }, [periodType, cleanPeriods.length]);

  // Show toggle if we have monthly data that can be rolled up
  const showPeriodToggle = periodType === 'monthly' && cleanPeriods.length > 1;

  // Determine available view options based on data type
  const availableViews = useMemo(() => {
    const views: Array<'yearly' | 'quarterly' | 'monthly'> = [];

    if (periodType === 'monthly') {
      // For monthly data, always allow all three views
      views.push('yearly', 'quarterly', 'monthly');
    } else if (periodType === 'quarterly') {
      // For quarterly data, allow yearly and quarterly
      views.push('yearly', 'quarterly');
    } else if (periodType === 'yearly') {
      // For yearly data, only yearly makes sense
      views.push('yearly');
    }

    return views;
  }, [periodType]);

  // Get current view data
  const currentRollups = useMemo(() => createRollups(cleanPeriods, periodView), [cleanPeriods, periodView, createRollups]);
  const currentPeriods = useMemo(() => {
    const keys = Object.keys(currentRollups);

    // Sort periods appropriately based on view type
    if (periodView === 'yearly') {
      return keys.sort((a, b) => parseInt(a) - parseInt(b));
    } else if (periodView === 'quarterly') {
      return keys.sort((a, b) => {
        const parseQuarter = (qStr: string) => {
          const match = qStr.match(/^Q(\d+)\s*(\d{4})/);
          if (!match) return { quarter: 0, year: 0 };
          return { quarter: parseInt(match[1]), year: parseInt(match[2]) };
        };

        const aParsed = parseQuarter(a);
        const bParsed = parseQuarter(b);

        if (aParsed.year !== bParsed.year) {
          return aParsed.year - bParsed.year;
        }
        return aParsed.quarter - bParsed.quarter;
      });
    } else if (periodView === 'monthly' && periodType === 'monthly') {
      // Sort aggregated months in calendar order
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return keys.sort((a, b) => {
        const aIndex = monthOrder.indexOf(a);
        const bIndex = monthOrder.indexOf(b);
        return aIndex - bIndex;
      });
    } else {
      // Individual monthly periods - already sorted from cleanPeriods
      return keys;
    }
  }, [currentRollups, periodView, periodType]);

  const currentRows = useMemo(() => createEnhancedRows(periodView), [periodView, createEnhancedRows]);
  const currentMetrics = useMemo(() => calculateMetrics(currentRows, currentPeriods), [currentRows, currentPeriods, calculateMetrics]);

  // Debug logging for yearly rollups
  React.useEffect(() => {
    console.log('Main Component Debug:', {
      periodType,
      cleanPeriods,
      periodView,
      showPeriodToggle,
      availableViews
    });

    if (periodView === 'yearly' && periodType === 'monthly') {
      console.log('Yearly Rollup Debug:', {
        originalMonthlyPeriods: cleanPeriods,
        yearlyRollups: currentRollups,
        yearlyPeriods: currentPeriods,
        currentRowsCount: currentRows.length,
        currentMetricsKeys: Object.keys(currentMetrics),
        sampleYearlyData: currentRows.slice(0, 2).map(row => ({
          itemName: row.itemName,
          yearlyTotals: currentPeriods.reduce((acc, year) => ({ ...acc, [year]: (row as any)[year] }), {})
        }))
      });
    }
  }, [periodView, periodType, cleanPeriods, currentRollups, currentPeriods, currentRows, currentMetrics, showPeriodToggle, availableViews]);

  // Format period display names
  const formatPeriodDisplay = (period: string): string => {
    if (periodView === 'yearly') return period; // Return full 4-digit year for yearly view
    if (periodView === 'quarterly') {
      const match = period.match(/^Q(\d+)\s*(\d{4})/);
      return match ? `Q${match[1]}-${match[2].slice(-2)}` : period;
    }
    if (periodView === 'monthly' && periodType === 'monthly') {
      // For aggregated monthly view, periods are already in "Jan", "Feb", "Mar" format
      return period;
    }
    // Individual monthly periods with year info
    const match = period.match(/^(\w+)\s*(\d{4})/i);
    if (match) {
      const monthMap: { [key: string]: string } = {
        'jan': 'Jan', 'january': 'Jan', 'feb': 'Feb', 'february': 'Feb',
        'mar': 'Mar', 'march': 'Mar', 'apr': 'Apr', 'april': 'Apr',
        'may': 'May', 'jun': 'Jun', 'june': 'Jun',
        'jul': 'Jul', 'july': 'Jul', 'aug': 'Aug', 'august': 'Aug',
        'sep': 'Sep', 'september': 'Sep', 'oct': 'Oct', 'october': 'Oct',
        'nov': 'Nov', 'november': 'Nov', 'dec': 'Dec', 'december': 'Dec'
      };
      const monthKey = match[1].toLowerCase();
      const shortMonth = monthMap[monthKey] || match[1];
      const shortYear = match[2].slice(-2);
      return `${shortMonth}-${shortYear}`;
    }
    return period;
  };

  // Enhanced delete handler with cascade logic
  const handleDeleteWithCascade = (rowsToDelete: string[]) => {
    if (periodView === 'yearly' && periodType === 'monthly') {
      // If deleting years from yearly view, cascade to remove corresponding months
      const rowsToDeleteData = currentRows.filter(row => rowsToDelete.includes(row.id));
      const yearsToDelete = new Set<string>();

      // Identify which years are being deleted by checking the row data
      rowsToDeleteData.forEach(row => {
        currentPeriods.forEach(year => {
          const value = (row as any)[year];
          if (value !== null && value !== undefined) {
            yearsToDelete.add(year);
          }
        });
      });

      console.log('Years being deleted from yearly view:', Array.from(yearsToDelete));

      // Now we need to identify and delete the corresponding monthly data
      if (yearsToDelete.size > 0) {
        console.log('Cascading deletion to remove monthly data for years:', Array.from(yearsToDelete));

        // Find monthly periods that belong to these years
        const monthlyPeriodsToDelete = cleanPeriods.filter(period => {
          // Extract year from monthly period (e.g., "Jan 2023" -> "2023")
          const match = period.match(/(\d{4})/);
          if (match) {
            const year = match[1];
            return yearsToDelete.has(year);
          }
          return false;
        });

        console.log('Monthly periods to be deleted:', monthlyPeriodsToDelete);

        // Show warning to user about cascade deletion
        if (monthlyPeriodsToDelete.length > 0) {
          const confirmMessage = `Deleting these yearly records will also remove ${monthlyPeriodsToDelete.length} monthly records for the following periods: ${monthlyPeriodsToDelete.join(', ')}. Continue?`;
          if (!window.confirm(confirmMessage)) {
            return; // User cancelled
          }
        }
      }
    }

    // Proceed with the deletion
    setSelectedRows(prev => [...prev, ...rowsToDelete]);
    setHasDeletions(true);
  };

  // Handler for saving changes
  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // If there are selected rows for deletion, handle them first
      if (selectedRows.length > 0 && handleBatchDelete) {
        await handleBatchDelete(selectedRows);
        setSelectedRows([]);
        setHasDeletions(false);
      }

      await handleSaveAllChanges(
        selectedHotel,
        financialData,
        'Profit & Loss Statement',
        setSuccess,
        setError,
        () => Promise.resolve() // Replace with actual fetch function if needed
      );

      // Clear selections and state after successful save
      setSelectedRows([]);
      setHasDeletions(false);

      // Show success message
      setSuccess('Changes saved successfully! Use the "Refresh Valuation" button to update valuation with new data.');

      // Clear success message after delay
      setTimeout(() => {
        setSuccess(null);
      }, 5000);

    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Update the handler for row selection changes
  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => {
    setSelectedRows(rowSelectionModel as string[]);
    setHasDeletions(rowSelectionModel.length > 0);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: '100%' }}>
        <Skeleton variant="rectangular" height={160} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={600} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Profit & Loss</Typography>
          <Typography variant="body1" color="text.secondary">{selectedHotel} • Financial Analysis</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Period View Toggle - Enhanced for dynamic views */}
          {showPeriodToggle && (
            <ToggleButtonGroup
              value={periodView}
              exclusive
              onChange={(_, newView) => newView && setPeriodView(newView)}
              size="small"
              sx={{
                mr: 1,
                '& .MuiToggleButton-root': {
                  color: 'text.primary',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    color: 'primary.contrastText',
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }
              }}
            >
              {availableViews.includes('yearly') && (
                <ToggleButton value="yearly" aria-label="yearly view">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AnalyticsIcon fontSize="small" />
                    Yearly
                  </Box>
                </ToggleButton>
              )}
              {availableViews.includes('quarterly') && (
                <ToggleButton value="quarterly" aria-label="quarterly view">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CompareArrowsIcon fontSize="small" />
                    Quarterly
                  </Box>
                </ToggleButton>
              )}
              {availableViews.includes('monthly') && (
                <ToggleButton value="monthly" aria-label="monthly view">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TableChartIcon fontSize="small" />
                    Monthly
                  </Box>
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          )}

          {(hasChanges || hasDeletions) && (
            <Button
              type="button"
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={isSaving}
              sx={{ height: 40, borderRadius: 1.5 }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: 'text.primary',
                borderColor: 'divider',
                '&.Mui-selected': {
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }
            }}
          >
            <ToggleButton value="table" aria-label="table view"><TableChartIcon sx={{ mr: 1 }} />Table</ToggleButton>
            <ToggleButton value="analysis" aria-label="analysis view"><ShowChartIcon sx={{ mr: 1 }} />Analysis</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Period Info Banner - Enhanced for yearly rollups */}
      {showPeriodToggle && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              onClick={() => {
                const currentIndex = availableViews.indexOf(periodView);
                const nextIndex = (currentIndex + 1) % availableViews.length;
                setPeriodView(availableViews[nextIndex]);
              }}
            >
              Switch to {availableViews[(availableViews.indexOf(periodView) + 1) % availableViews.length].charAt(0).toUpperCase() + availableViews[(availableViews.indexOf(periodView) + 1) % availableViews.length].slice(1)}
            </Button>
          }
        >
          <Typography variant="body2">
            {periodView === 'yearly'
              ? `Showing yearly totals (${currentPeriods.length} years). All monthly data has been aggregated by year: ${currentPeriods.join(', ')}.`
              : periodView === 'quarterly'
                ? `Showing quarterly summary (${currentPeriods.length} quarters). Monthly data grouped into quarters from ${cleanPeriods.length} periods.`
                : periodView === 'monthly' && periodType === 'monthly'
                  ? `Showing monthly aggregates (${currentPeriods.length} months). Each month combines data from all years (${cleanPeriods.length} total periods).`
                  : `Showing detailed monthly view (${cleanPeriods.length} periods). Click Yearly to see annual totals or Quarterly for quarterly summaries.`
            }
          </Typography>
        </Alert>
      )}

      {/* Metrics Dashboard Child Component */}
      <FinancialMetricsDashboard
        financialData={financialData}
        financialMetrics={currentMetrics}
        years={currentPeriods}
        periodType={periodType}
      />

      {/* Conditional rendering based on view mode */}
      {viewMode === 'table' ? (
        <>
          {/* Data Table Child Component */}
          <FinancialDataTable
            rows={currentRows}
            years={currentPeriods}
            financialMetrics={currentMetrics}
            categories={financialData?.categories || []}
            loading={loading}
            saving={saving}
            handleDeleteClick={handleDeleteClick}
            handleBatchDelete={handleBatchDelete}
            onEnhancedDelete={handleDeleteWithCascade}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            selectedRows={selectedRows}
            onRowSelectionChange={handleRowSelectionChange}
          />

          {/* Add Transaction Form Child Component */}
          <AddTransactionForm
            formState={{ category: addFormCategory, itemName: addFormItemName, year: addFormYear, amount: addFormAmount }}
            setters={{ setCategory: setAddFormCategory, setItemName: setAddFormItemName, setYear: setAddFormYear, setAmount: setAddFormAmount }}
            categories={financialData?.categories || []}
            saving={saving}
            onSubmit={handleAddNewItem}
          />
        </>
      ) : (
        <>
          {(() => {
            console.log('Analysis View Data:', { financialData, gridRows: currentRows, enhancedRows: currentRows, financialMetrics: currentMetrics });
            return (
              <ProfitLossAnalysis
                financialData={{
                  years: currentPeriods,
                  categories: financialData?.categories || [],
                  benchmarks: financialData?.benchmarks
                }}
                gridRows={currentRows}
              />
            );
          })()}
        </>
      )}
    </Box>
  );
};

export default ProfitLossStatement;